/**
 * Flash Messages Context
 * Provides global state management for flash messages across the app
 */

import React, { createContext, useContext, useReducer, useCallback, ReactNode } from 'react';
import { FlashMessage, FlashMessagesState, FlashMessagesAction } from '../types/flashMessages';
import { getStorageItem, setStorageItem } from '../utils/localStorage';

// Initial state
const initialState: FlashMessagesState = {
  messages: [],
};

// Reducer
const flashMessagesReducer = (state: FlashMessagesState, action: FlashMessagesAction): FlashMessagesState => {
  switch (action.type) {
    case 'ADD_MESSAGE':
      return {
        ...state,
        messages: [...state.messages, action.payload],
      };
    case 'REMOVE_MESSAGE':
      return {
        ...state,
        messages: state.messages.filter(message => message.id !== action.payload),
      };
    case 'CLEAR_ALL_MESSAGES':
      return {
        ...state,
        messages: [],
      };
    default:
      return state;
  }
};

// Context interface
interface FlashMessagesContextType {
  messages: FlashMessage[];
  addMessage: (message: Partial<FlashMessage>) => void;
  removeMessage: (id: string) => void;
  clearAllMessages: () => void;
}

// Create context
const FlashMessagesContext = createContext<FlashMessagesContextType | undefined>(undefined);

// Provider props
interface FlashMessagesProviderProps {
  children: ReactNode;
}

// Provider component
export const FlashMessagesProvider: React.FC<FlashMessagesProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(flashMessagesReducer, initialState);

  // Generate unique ID for messages
  const generateId = useCallback(() => {
    return `flash-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // Add message
  const addMessage = useCallback((messageData: Partial<FlashMessage>) => {
    // Check if this is a persistent message that has been dismissed
    if (messageData.persistent && messageData.id) {
      const dismissedKey = `gekkovet-copilot-${messageData.id}-dismissed`;
      if (getStorageItem(dismissedKey)) {
        return; // Don't show if previously dismissed
      }
    }

    const message: FlashMessage = {
      id: messageData.id || generateId(),
      type: messageData.type || 'info',
      title: messageData.title,
      message: messageData.message || '',
      sticky: messageData.sticky || false,
      timeout: messageData.timeout || 5000,
      dismissible: messageData.dismissible !== false, // Default to true
      persistent: messageData.persistent || false,
    };

    dispatch({ type: 'ADD_MESSAGE', payload: message });

    // Auto-dismiss non-sticky messages
    if (!message.sticky && message.timeout && message.timeout > 0) {
      setTimeout(() => {
        dispatch({ type: 'REMOVE_MESSAGE', payload: message.id });
      }, message.timeout);
    }
  }, [generateId]);

  // Remove message
  const removeMessage = useCallback((id: string) => {
    // Find the message to check if it's persistent
    const message = state.messages.find(m => m.id === id);
    
    if (message && message.persistent) {
      // Store dismissal in localStorage
      const dismissedKey = `gekkovet-copilot-${id}-dismissed`;
      setStorageItem(dismissedKey, 'true');
    }

    dispatch({ type: 'REMOVE_MESSAGE', payload: id });
  }, [state.messages]);

  // Clear all messages
  const clearAllMessages = useCallback(() => {
    dispatch({ type: 'CLEAR_ALL_MESSAGES' });
  }, []);

  const value: FlashMessagesContextType = {
    messages: state.messages,
    addMessage,
    removeMessage,
    clearAllMessages,
  };

  return (
    <FlashMessagesContext.Provider value={value}>
      {children}
    </FlashMessagesContext.Provider>
  );
};

// Hook to use the context
export const useFlashMessagesContext = (): FlashMessagesContextType => {
  const context = useContext(FlashMessagesContext);
  if (context === undefined) {
    throw new Error('useFlashMessagesContext must be used within a FlashMessagesProvider');
  }
  return context;
};
